// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat3_iloczynowa_types/TransformGeneralToFactoredTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

/* =============================================================
   TransformGeneralToFactoredTask
   f(x)=ax^2+bx+c  →  f(x)=a(x-x1)(x-x2)
   Generator: Δ>0 (ładny pierwiastek) ~5/6, Δ=0 ~1/6.
   Styl obliczeń jak w SolveQuadraticTwoSolutionsTask (kolumny x1/x2).
============================================================= */

/* ---------- helpers ---------- */
function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}
const par = (v) => (v < 0 ? `(${v})` : `${v}`);

function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simplifyFrac(n, d){
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function formatMixed(n, d){
  const s = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${s}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${s}\\dfrac{${N}}{${D}}`;
  return `${s}${whole}\\dfrac{${rem}}{${D}}`;
}
function factorOf(n, d){ // (x - r) z r w ładnym latexie (finalny zapis)
  const isNeg = n < 0;
  const abs = formatMixed(Math.abs(n), d);
  const r   = formatMixed(n, d);
  return isNeg ? `\\big(x + ${abs}\\big)` : `\\big(x - ${r}\\big)`;
}
// PODSTAWIENIE do wzoru: jeśli r < 0, pokaż „x - (−r)”
function subFactor(frac){
  const r = formatMixed(frac.n, frac.d);
  return frac.n < 0
    ? `\\big(x-(${r})\\big)`
    : `\\big(x-${r}\\big)`;
}

/* ---------- generator: Δ>0 (5/6) lub Δ=0 (1/6) ---------- */
export function generateTask() {
  const a = randInt(-6, 6, new Set([0]));
  const deltaZero = Math.floor(Math.random() * 6) === 0; // ~1/6 szansy na Δ=0

  let r1, r2;
  if (deltaZero) {
    r1 = randInt(-6, 6, new Set([0]));
    r2 = r1;
  } else {
    r1 = randInt(-6, 6, new Set([0]));
    do { r2 = randInt(-6, 6, new Set([0, r1])); } while (r2 === r1);
  }

  const b = -a * (r1 + r2);
  const c =  a * r1 * r2;

  const delta = b*b - 4*a*c;                     // = a^2 (r1 - r2)^2
  const sqrtDelta = Math.abs(a) * Math.abs(r1 - r2); // 0 dla Δ=0

  const x1Frac = simplifyFrac(r1, 1);
  const x2Frac = simplifyFrac(r2, 1);

  const quadLatex = (() => {
    const term = (coeff, base, omitOne=false) => {
      if (coeff === 0) return "";
      const sign = coeff > 0 ? " + " : " - ";
      const abs = Math.abs(coeff);
      const num = (abs === 1 && omitOne) ? "" : String(abs);
      return `${sign}${num}${base}`;
    };
    const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
    let out = `${aCoeff}x^2${term(b,"x",true)}`;
    if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
    return out;
  })();

  return {
    type: "TransformGeneralToFactoredTask",
    data: {
      a, b, c, delta, sqrtDelta,
      x1: r1, x2: r2,
      x1Frac, x2Frac,
      quadLatex,
      deltaZero
    }
  };
}

/* ---------- treść ---------- */
export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div> Dana jest funkcja w postaci ogólnej: <InlineMath math={`f(x) = ${data.quadLatex}`} /></div>
      <div> Zapisz ją w postaci iloczynowej.</div>
    </div>
  );
}

/* ---------- input (tylko prawa strona) ---------- */
function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    for (const k of ["raw","value","text","input","expr","equation","latex"]) {
      if (typeof val[k] === "string" && val[k].trim() !== "") return val[k];
    }
  }
  return "";
}
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = getRaw(userAnswer);
  const hasText = !!String(raw).trim();

  return (
    <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
      <div><InlineMath math={'f(x) ='} /></div>
      <EquationInput
        value={raw}
        onChange={(val) => onChange(val)}
        disabled={disabled}
        placeholder="np. 3(x-2)(x+1) lub 3(x-2)^2"
        showPreview={false}
      />
      {hasText && (
        <div style={{ opacity: 0.85 }}>
          <span style={{ marginRight: 6 }}>Podgląd:</span>
          <InlineMath math={`f(x) = ${raw}`} />
        </div>
      )}
    </div>
  );
}

/* ---------- parser factored ---------- */
const NUM = '(?:\\d+\\/\\d+|\\d*\\.\\d+|\\d+)';
function parseNumber(s) {
  if (!s) return null;
  s = s.replace(/,/g, "."); // akceptuj przecinek
  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(s)) return parseFloat(s);
  const m = s.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (m) { const p=parseInt(m[1],10), q=parseInt(m[2],10); if (!q) return null; return p/q; }
  return null;
}
function parseFactored(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return null;
  s = s.replace(/\\cdot/g, "").replace(/\s+/g, "");
  // dwa czynniki
  let re2 = new RegExp(`^([+\\-]?${NUM})?\\(x([+-])(${NUM})\\)\\(x([+-])(${NUM})\\)$`,"i");
  let m = s.match(re2);
  if (m) {
    const aStr = m[1];
    let a;
    if (aStr == null || aStr === "") a = 1;
    else if (aStr === "-") a = -1;
    else { a = parseNumber(aStr); if (a == null) return null; }
    const s1 = m[2] === "+" ? +1 : -1, t1 = parseNumber(m[3]); if (t1==null) return null;
    const s2 = m[4] === "+" ? +1 : -1, t2 = parseNumber(m[5]); if (t2==null) return null;
    return { a, r1: (s1===+1?-t1:t1), r2: (s2===+1?-t2:t2) };
  }
  // kwadrat jednego
  let re1 = new RegExp(`^([+\\-]?${NUM})?\\(x([+-])(${NUM})\\)\\^2$`,"i");
  m = s.match(re1);
  if (m) {
    const aStr = m[1];
    let a;
    if (aStr == null || aStr === "") a = 1;
    else if (aStr === "-") a = -1;
    else { a = parseNumber(aStr); if (a == null) return null; }
    const sgn = m[2] === "+" ? +1 : -1, t = parseNumber(m[3]); if (t==null) return null;
    const r = (sgn===+1?-t:t);
    return { a, r1: r, r2: r };
  }
  return null;
}

/* ---------- weryfikacja numeryczna (fallback) ---------- */
function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}
function powToJS(expr) { return expr.replace(/\^/g, "**"); }
function safeEvalRHS(rhs, x) {
  let s = String(rhs ?? "").trim();
  if (!s) return NaN;
  s = s.replace(/,/g, ".");
  s = explicitMultiply(s);
  s = powToJS(s);
  s = s.replace(/x/g, `(${x})`);
  try { /* eslint-disable no-new-func */
    const val = Function(`"use strict"; return (${s});`)();
    return Number.isFinite(val) ? val : NaN;
  } catch { return NaN; }
}
function numericEq(userRHS, a, r1, r2, tol=1e-3) {
  const xs = [-3,-1,0,1,2,4];
  for (const x of xs) {
    const u = safeEvalRHS(userRHS, x);
    if (!Number.isFinite(u)) return false;
    const ref = a*(x - r1)*(x - r2);
    if (Math.abs(u - ref) > tol) return false;
  }
  return true;
}

/* ---------- check ---------- */
export function checkAnswer(arg1, arg2) {
  const task = arg1 && arg1.data ? arg1 : arg2;
  const answer = arg1 && arg1.data ? arg2 : arg1;

  const parsed = parseFactored(getRaw(answer));
  if (parsed) {
    const aOK = Math.abs(parsed.a - task.data.a) < 1e-9;
    const given  = [parsed.r1, parsed.r2].sort((u,v)=>u-v);
    const target = [task.data.x1, task.data.x2].sort((u,v)=>u-v);
    const rootsOK = Math.abs(given[0]-target[0])<1e-9 && Math.abs(given[1]-target[1])<1e-9;
    if (aOK && rootsOK) return true;
  }
  return numericEq(getRaw(answer), task.data.a, task.data.x1, task.data.x2, 1e-3);
}

/* ---------- user answer preview ---------- */
export function renderUserAnswer(arg1, arg2) {
  const ua = (arg1 && arg1.data) ? arg2 : arg1;
  const raw = getRaw(ua);
  if (!raw.trim()) return <i>(brak odpowiedzi)</i>;
  return <div style={{ marginTop: 6, textAlign: "left" }}><InlineMath math={`f(x) = ${raw}`} /></div>;
}

/* ---------- official answer ---------- */
export function renderAnswer({ data }) {
  const aStr = Math.abs(data.a) === 1 ? (data.a === -1 ? "-" : "") : String(data.a);

  if (data.deltaZero) {
    const f = factorOf(data.x1Frac.n, data.x1Frac.d);
    return (
      <div style={{ marginTop: 6, textAlign: "left" }}>
        <InlineMath math={`f(x) = ${aStr}${f}^{2}`} />
      </div>
    );
  }

  const f1 = factorOf(data.x1Frac.n, data.x1Frac.d);
  const f2 = factorOf(data.x2Frac.n, data.x2Frac.d);
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${aStr}${f1}${f2}`} />
    </div>
  );
}

/* ---------- explanation ---------- */
export function renderExplanation({ data }) {
  const { a, b, c, delta, sqrtDelta, x1Frac, x2Frac, deltaZero } = data;
  const twoA = 2*a;

  // Krok 1
  const step1 = String.raw`a = ${a},\quad b = ${b},\quad c = ${c}`;

  // Krok 2
  const step2a = String.raw`\Delta = b^{2} - 4ac`;
  const step2b = String.raw`\Delta = ${par(b)}^{2} - 4\cdot${par(a)}\cdot${par(c)} = ${b*b} - ${4*a*c < 0 ? `(${4*a*c})` : `${4*a*c}`} = ${delta}`;
  const step2c = String.raw`\sqrt{\Delta} = ${sqrtDelta}`;

  // Krok 3 — kolumny albo pojedynczy pierwiastek
  const minusBLatex = b < 0 ? `-(${b})` : `-${b}`;

  let bodyRoots;
  if (deltaZero) {
    const xLines = String.raw`
\begin{aligned}
x_1 = x_2 & = \dfrac{-b}{2a} \\[6pt]
          & = \dfrac{${minusBLatex}}{2\cdot ${par(a)}} \\[6pt]
          & = ${formatMixed(x1Frac.n, x1Frac.d)}
\end{aligned}`;
    bodyRoots = (
      <div>
        <BlockMath math={xLines} />
      </div>
    );
  } else {
    const x1Lines = String.raw`
\begin{aligned}
x_1 &= \dfrac{-b - \sqrt{\Delta}}{2a} \\[6pt]
x_1 &= \dfrac{${minusBLatex} - ${sqrtDelta}}{2\cdot ${par(a)}} \\[6pt]
x_1 &= \dfrac{${-b - sqrtDelta}}{${twoA}} \\[6pt]
x_1 &= ${formatMixed(x1Frac.n, x1Frac.d)}
\end{aligned}`;
    const x2Lines = String.raw`
\begin{aligned}
x_2 &= \dfrac{-b + \sqrt{\Delta}}{2a} \\[6pt]
x_2 &= \dfrac{${minusBLatex} + ${sqrtDelta}}{2\cdot ${par(a)}} \\[6pt]
x_2 &= \dfrac{${-b + sqrtDelta}}{${twoA}} \\[6pt]
x_2 &= ${formatMixed(x2Frac.n, x2Frac.d)}
\end{aligned}`;
    bodyRoots = (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", alignItems: "start" }}>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}><InlineMath math={'x_1'} /></div>
          <BlockMath math={x1Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}><InlineMath math={'x_2'} /></div>
          <BlockMath math={x2Lines} />
        </div>
      </div>
    );
  }

  // Wzór postaci iloczynowej + podstawienie (przed finalem)
  const aStr = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  const r1Str = formatMixed(x1Frac.n, x1Frac.d);
  const r2Str = formatMixed(x2Frac.n, x2Frac.d);

  const template = deltaZero
    ? String.raw`f(x)=a\,(x-x_1)^{2}`
    : String.raw`f(x)=a\,(x-x_1)\,(x-x_2)`;

  // >>> TU PODMIANA Z NAWIASAMI DLA UJEMNYCH PIERWIASTKÓW <<<
  const substitution = deltaZero
    ? String.raw`f(x)=${aStr}\,${subFactor(x1Frac)}^{2}`
    : String.raw`f(x)=${aStr}\,${subFactor(x1Frac)}\,${subFactor(x2Frac)}`;

  // Finalny zapis (ładne nawiasy „x ± …”)
  const f1 = factorOf(x1Frac.n, x1Frac.d);
  const f2 = factorOf(x2Frac.n, x2Frac.d);
  const factored = deltaZero ? `${aStr}${f1}^{2}` : `${aStr}${f1}${f2}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Współczynniki:</div>
      <BlockMath math={step1} />

      <div><strong>Krok 2.</strong> Liczymy deltę:</div>
      <BlockMath math={step2a} />
      <BlockMath math={step2b} />
      <BlockMath math={step2c} />

      <div><strong>Krok 3.</strong> Obliczamy pierwiastki:</div>
      {bodyRoots}

      <div><strong>Krok 4.</strong> Podstawiamy do wzoru postaci iloczynowej:</div>
      <BlockMath math={template} />
      <BlockMath math={substitution} />

      <div><strong>Krok 5.</strong> Zapisujemy postać iloczynową:</div>
      <BlockMath math={`f(x) = ${factored}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderUserAnswer,
  renderAnswer,
  renderExplanation,
};
